import React from 'react';
import { clsx } from 'clsx';
import { BanknotesIcon } from '@heroicons/react/24/outline';
import type { PositionDetail } from '@/types/trades.types';
import { Panel, PanelHeader, PanelBody } from '@/components/ui/Panel';
import { formatDecimalString, toDecimalString } from '@/utils/decimal-display';
import { effectiveRates } from '@/services/trades/effective-rate';
import { pricedByPool } from '@/utils/trades/cash-pool-rate';
import {
  deriveDecompositionPresence,
  hasSettlementDailyReturn,
  isNullOrZeroDecimal,
  isTerminalStatus,
} from './analytics-economics';

interface PnlTableProps {
  trade: PositionDetail;
}

/** One informational leg row (label + sub-label, right-aligned magnitude /day). */
function LegRow({ label, small, value }: { label: string; small: string; value: string }) {
  return (
    <tr>
      <td className="border-b border-lr-border py-[11px] pr-2 align-baseline">
        <span className="text-sm text-lr-fg-2">{label}</span>
        <span className="block text-[11.5px] font-normal text-lr-faint">{small}</span>
      </td>
      <td className="whitespace-nowrap border-b border-lr-border py-[11px] text-right text-sm font-semibold text-lr-fg">
        {value}
        {value !== '—' && <span className="ml-0.5 text-[11.5px] font-normal text-lr-faint">/day</span>}
      </td>
    </tr>
  );
}

/**
 * Daily P&L decomposition (prototype `.pnl`). Net / day is the signed
 * `daily_earnings` (rate × CV × direction) — the same number used for the
 * KPI hero and the cumulative chart projection, so the three never disagree.
 *
 * Rendering rule is mode-driven:
 *   * REBATE (GMSLA rebate or GMRA repo) — no economic decomposition exists
 *     for a single rebate/repo rate, so the card shows only the Net /day line.
 *     The historical `fee_income` / `cost_of_collateral` legs were spread-leg
 *     artifacts pre-LR-399 and are intentionally suppressed.
 *   * FEE — fee on gross + cost on CV that net to the daily P&L (feedback §2d,
 *     LR-398). Three rows: Fee income, Cash Pool Interest Accrual (LR-631,
 *     renamed from Cost of collateral), Net.
 *
 * Net tone derives from the ROUNDED string so a "-0.004" → "$0.00" isn't red.
 */
export function PnlTable({ trade }: PnlTableProps) {
  const currency = trade.currency;
  const presence = deriveDecompositionPresence(trade);
  const isFeeMode = trade.rate_entry_mode === 'fee';
  // Mirror KpiStrip's isTerminal — a matured/closed/cancelled position has no
  // live daily accrual, so the decomp Net /day and footer "Effective annual"
  // must follow the KPI hero in freezing (the 87d96de mask covered only the
  // hero, leaving the decomp card to read live −$176.76/day on a Matured
  // trade — audit N10). Single source: analytics-economics.TERMINAL_STATUSES
  // (LR-208 r5 M3).
  const isTerminal = isTerminalStatus(trade.status);

  // Honest empty rule (HON-04): null OR zero daily renders "—", not "$0.00".
  // Terminal trades also render "—" so the card can't lie a live earning on a
  // position that has stopped accruing.
  // LR-628: a floating rebate accrues on today's fixing, so every "per day"
  // figure on this page must be priced on the effective rate — the three cards
  // (KpiStrip / StickySummary / PnlTable) stay in lock-step by all reading it.
  const { dailyEarnings, dailyEarningsConverted, totalRate, annualEarnings } =
    effectiveRates(trade);
  const hasDaily = !isNullOrZeroDecimal(dailyEarnings);
  // LR-459 → LR-474: for cross-currency trades the SETTLEMENT figure is the
  // headline Net/day and the security-ccy original becomes the `≈` twin —
  // matches KpiStrip + StickySummary; the three MUST stay in lock-step (change
  // all together). Sign semantics survive the swap: fx_rate > 0, so the
  // converted figure carries the same sign. Twin suppressed when Net is dashed
  // (terminal / null-or-zero daily) so it can't show a live figure on a frozen one.
  const hasSettlementDaily = hasSettlementDailyReturn(trade);
  const displayedNet = isTerminal || !hasDaily
    ? '—'
    : hasSettlementDaily
      ? formatDecimalString(dailyEarningsConverted, {
          currency: trade.settlement_currency || undefined,
          dp: 2,
        })
      : formatDecimalString(dailyEarnings, { currency, dp: 2 });
  const isDashNet = displayedNet === '—';
  const isNetNegative = !isDashNet && displayedNet.startsWith('-');
  const nativeNetTwin =
    !isDashNet && hasSettlementDaily
      ? formatDecimalString(dailyEarnings, { currency, dp: 2 })
      : null;

  const totalRateStr =
    toDecimalString(totalRate) != null ? `${formatDecimalString(totalRate, { dp: 2 })}%` : null;
  const annualStr =
    toDecimalString(annualEarnings) != null
      ? `${formatDecimalString(annualEarnings, { currency, dp: 2 })}/yr`
      : null;

  return (
    <Panel>
      <PanelHeader
        icon={<BanknotesIcon className="h-[15px] w-[15px] flex-none text-lr-muted" aria-hidden="true" />}
        title="Daily PnL decomposition"
        meta={
          !isFeeMode
            ? 'per day, at agreed rate'
            : trade.pool_accrues_interest
              ? 'per day, fee only'
              : 'per day, fee netted against cost'
        }
      />
      <PanelBody>
        <table className="lr-num w-full border-collapse">
          <tbody>
            {isFeeMode && (
              <>
                <LegRow
                  label="Fee income"
                  small="lending fee leg"
                  value={presence.hasFee ? formatDecimalString(trade.fee_income, { currency, dp: 2 }) : '—'}
                />
                {trade.pool_accrues_interest ? (
                  // Not a zero: the cash does earn interest, in its pool, and the
                  // customer ruled that figure cannot be split per trade (24.08 p.2).
                  <LegRow
                    label="Cash Pool Interest Accrual"
                    small="accrued by the cash pool, see Cash Pool Interest in the statement"
                    value="—"
                  />
                ) : presence.hasCost && (
                  <LegRow
                    label="Cash Pool Interest Accrual"
                    // The sub-label states the mechanism, so it has to be true
                    // of THIS trade: a fee trade booked before LR-631 made the
                    // pool mandatory still funds its collateral off the booked
                    // benchmark, with no pool behind the figure. So does one
                    // whose pool the backend refuses to price off - `pricedByPool`
                    // carries that exclusion (dp review, Low 2).
                    small={
                      pricedByPool(trade)
                        ? 'interest on the collateral in its cash pool'
                        : 'funding of posted collateral'
                    }
                    // LR-631: the effective twin, for the same reason Net/day
                    // reads one — this card nets Fee against Cost, and the Net
                    // above is priced on today's pool rate. The booked cost
                    // beside it would leave the three rows not adding up on
                    // exactly the pooled fee trades the rename is about.
                    value={formatDecimalString(
                      trade.effective_cost_of_collateral ?? trade.cost_of_collateral,
                      { currency, dp: 2 },
                    )}
                  />
                )}
              </>
            )}
            <tr>
              <td className={clsx(isFeeMode && 'border-t-2 border-lr-fg pt-3')}>
                <span className="text-sm font-bold text-lr-fg">Net / day</span>
              </td>
              <td
                className={clsx(
                  'lr-num whitespace-nowrap text-right text-[18px] font-extrabold',
                  isFeeMode && 'border-t-2 border-lr-fg pt-3',
                  isDashNet ? 'text-lr-muted' : isNetNegative ? 'text-lr-neg' : 'text-lr-pos',
                )}
              >
                {displayedNet}
                <span className="ml-0.5 text-[11.5px] font-normal text-lr-faint">/day</span>
                {nativeNetTwin && (
                  <span className="block text-[11.5px] font-normal text-lr-faint">
                    ≈ {nativeNetTwin}
                    <span className="ml-0.5">/day</span>
                  </span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
        <p className="mt-3 text-[11.5px] leading-relaxed text-lr-faint">
          {isTerminal ? (
            // A matured / closed / cancelled position no longer accrues — the
            // "Effective annual …" projection would be a live computation on a
            // frozen trade. Mirror the KPI hero ("—" + "closed") with an honest
            // closing footer instead.
            'Position closed — daily accrual stopped.'
          ) : (
            <>
              {/* Drop the "(we pay/we earn)" parenthetical when Net renders as
                  "—" (null OR zero daily). The clause infers direction from
                  isNetNegative, which is false for the dash — without the
                  guard the prose narrates "we earn" on a trade that hasn't
                  yet been priced (fresh trades, rate-not-set, fee=cost). */}
              Net = signed daily PnL{isDashNet ? '' : ` (${isNetNegative ? 'we pay' : 'we earn'})`}.
              {/* Drop the netted total-rate (e.g. "−1.30 %" = fee 4 % − rebate
                  5.30 %) for fee-mode footer the same way it's dropped from the
                  KPI hero + Rate build-up — the single resolved rate isn't an
                  economically meaningful number on a fee trade. The annual $/yr
                  is still informative, so we surface it without the % label. */}
              {isFeeMode
                ? annualStr && (
                    <>
                      {' '}
                      Effective annual <span className="lr-num">{annualStr}</span>
                      {/* LR-474 r1 #6: the headline Net/day above is settlement-ccy
                          for cross-ccy trades while the annual stays native — say so. */}
                      {nativeNetTwin ? ' (security ccy)' : ''}.
                    </>
                  )
                : totalRateStr && (
                    <>
                      {' '}
                      Effective annual <span className="lr-num font-semibold text-lr-fg-2">{totalRateStr}</span>
                      {annualStr && <span className="lr-num"> · {annualStr}</span>}
                      {annualStr && nativeNetTwin ? ' (security ccy)' : ''}.
                    </>
                  )}
            </>
          )}
        </p>
      </PanelBody>
    </Panel>
  );
}
